import { FC, useRef, useEffect } from 'react';
import Link from 'next/link';
import Layout from '@/components/layout/Layout';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import RatingStars from '@/components/ui/book-info/rating-stars/RatingStars';
import LitresWidget from '@/components/ui/litres-widget/LitresWidget';
import ReadBanner from '@/components/ui/banners/read-banner/ReadBanner';
import Pagination from '@/components/ui/pagination/Pagination';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { getAuthorFullName } from '@/helpers/helpers';
import { IBookDetailData } from '@/interfaces/bookDetail.interface';
import { IRead } from '@/interfaces/read.interface';
import { hideImgOnError } from '@/helpers/helpers';
import { DEV_ABSOLUTE_URL } from '@/helpers/variables';
import styles from './ReadDetail.module.scss';

interface ReviewsDetailPage {
  book: IBookDetailData;
  read: IRead;
  pageNumber?: string;
}

const ReadDetail: FC<ReviewsDetailPage> = ({ book, read, pageNumber }) => {
  const bookData = book.book;
  const { first_name, middle_name, last_name } = book.author;
  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);

  const bookUrl = useGetUrlPath();
  const currentPage = read.page;
  const pagesCount = read.pages;

  //Временное решение пока нет алиаса для кнопки литрес
  const replaceId = new RegExp(`-${book.id}`);
  const replaceAliasWord = new RegExp('kniga-');
  const litresAliasCutId = book.alias.replace(replaceId, '');
  const litresAlias = litresAliasCutId.replace(replaceAliasWord, '');

  const refReadContent = useRef<HTMLDivElement>(null);

  const SEO_TITLE = `Читать книгу ${bookData.title} (${authorFullName}) онлайн бесплатно на Bookz${currentPage > 1 ? ` (${currentPage}-ая страница книги)` : ``}`;
  const SEO_DESCRIPTION = `Читать бесплатно текст книги "${bookData.title}" автора ${authorFullName}${currentPage > 1 ? ` (${currentPage}-ая страница книги)` : ``} :: Бесплатные книги в электронном варианте :: BookZ.ru`;
  const SEO_CANONICAL = `/authors/${book.author.alias}/${book.alias}/read`;
  const SEO_COVER = `/covers/${bookData.external_id}.jpg`;

  const createMarkup = (c: string) => {
    return { __html: c };
  };

  let renderBannersOnce = true;

  useEffect(() => {
    // Баннер yandex
    if (renderBannersOnce) {
      const paragraphs = refReadContent.current?.querySelectorAll('p');
      const txtImages = refReadContent.current?.querySelectorAll('img');

      if (pageNumber === undefined && txtImages !== undefined && txtImages.length > 0) {
        txtImages[0].style.display = 'none';
      }

      if (paragraphs !== undefined && paragraphs?.length > 0) {
        const middleParagraph = paragraphs[Math.ceil(paragraphs.length / 2)];

        const renderMiddleBanner = (idName: string, className: string) => {
          const yandexBanner = document.createElement('div');
          yandexBanner.setAttribute('id', idName);
          yandexBanner.setAttribute('data', className);

          middleParagraph.after(yandexBanner);
        };
        renderMiddleBanner('yandex_rtb_R-A-745623-17', 'read-banner-desktop');
        renderMiddleBanner('yandex_rtb_R-A-745623-16', 'read-banner-mobile');
      }
    }
    renderBannersOnce = false;
  });

  return (
    <Layout
      title={SEO_TITLE}
      description={SEO_DESCRIPTION}
      canonicalHref={SEO_CANONICAL}
      cover={SEO_COVER}
      pageHref={bookUrl}
      isNoSideBars
      breadcrumbs={<BookBreadcrumbs bookTitle={book.book.title} bookAlias={book.alias} authorFullName={authorFullName} authorAlias={book.author.alias} genres={book.genres} isExtraPage extraPageName="Читать онлайн" />}
      robotsNoFollow
    >
      <div className={styles.readOnlineCard}>
        <div className={styles.readOnlineCard__img}>
          <div className={`${styles.readOnlineCard__title} ${styles.readOnlineCard__title_mob}`}>{bookData.title}</div>
          <div className={styles.readOnlineCard__item}>
            <div className={styles.readOnlineCard__imgWrap}>
              <span className={styles.readOnlineCard__pic}>
                <img width="300" height="460" src={`${DEV_ABSOLUTE_URL}/covers/${bookData.external_id}.jpg`} alt={bookData.title} onError={hideImgOnError} />
                {book.book.allow_full_free > 0 && <span className={styles.readOnlineCard__imageFullLabel}>Полная версия</span>}
              </span>

              <div className={styles.readOnlineCard__imgControls}>
                <div className={styles.readOnlineCard__ratingTitle}>Оценить:</div>
                <RatingStars rating={book.rating} title={bookData.title} id={book.id} />
              </div>

              <div className="litresWidget">
                <h3 className="litresWidget__title">Полная версия:</h3>
                <div>
                  <LitresWidget bookTitle={bookData.title} authorName={authorFullName} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.readOnlineCard__desc}>
          <h1 className={styles.readOnlineCard__title}>{bookData.title}</h1>
          <div className={styles.readOnlineCard__txt}>
            <div data-book-reader-container className={styles.readBookContainer}>
              <div data-book-reader ref={refReadContent} className={styles.readOnlineCard__txt} dangerouslySetInnerHTML={createMarkup(read.content)}></div>
              <ReadBanner />
            </div>
          </div>
          {currentPage === pagesCount && !book.hide_litres_data && book.book.allow_full_free === 0 && (
            <div className={styles.readOnlineCard__trialBlock}>
              <p>Вы ознакомились с фрагментом книги.</p>
              <p>Для бесплатного чтения открыта только часть текста.</p>
              <p>Приобретайте полный текст книги у нашего партнера:</p>
              <br />
              <Link prefetch={false} target="_blank" className="mainBtn" href={`https://www.litres.ru/pages/biblio_book/?art=${book.book.external_id}&lfrom=1142890029`}>
                Полная версия книги
              </Link>
              {book.files && book.files.length > 0 && (
                <>
                  <br />
                  <br />
                  <p>{`Всего ${book.files.length} форматов`}</p>
                </>
              )}
            </div>
          )}
          {book.hide_litres_data && (
            <>
              <br />
              <p>Вы ознакомились с фрагментом книги.</p>
            </>
          )}
        </div>
      </div>
      <div className="clearfix"></div>
      <Pagination pagesCount={pagesCount ?? 0} postName={`authors/${book.author.alias}/${book.alias}/read/page=`} />
    </Layout>
  );
};

export default ReadDetail;
